/**
 * Iter XX.60 — Realtime Voice: WebRTC client.
 *
 * Direct-call architecture:
 *   1. POST /api/realtime/session — server mints ephemeral client_secret.
 *   2. Browser: getUserMedia → RTCPeerConnection → createOffer.
 *   3. POST /api/realtime/negotiate — backend proxies SDP to OpenAI.
 *   4. setRemoteDescription(answer) → connection live.
 *   5. Data channel `oai-events` streams JSON events from the model:
 *        input_audio_buffer.speech_started / stopped
 *        response.audio.delta / done
 *        conversation.item.input_audio_transcription.completed
 *        response.audio_transcript.done
 *        response.cancelled
 *   6. Finalized transcripts POST /api/realtime/turn (idempotent).
 *   7. On end/error POST /api/realtime/end.
 *
 * The permanent OPENAI_API_KEY never touches this file. The ephemeral
 * client_secret lives only in memory for the duration of the call.
 */
import api from "./api";

const HEARTBEAT_INTERVAL_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 1;

export function isRealtimeCapable() {
  return typeof window !== "undefined"
    && typeof window.RTCPeerConnection !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function";
}

export function createRealtimeVoiceSession({ onEvent }) {
  let pc = null;
  let dc = null;
  let localStream = null;
  let audioEl = null;
  let realtimeSessionId = null;
  let clientSecret = null;
  let heartbeatTimer = null;
  let hardCapTimer = null;
  let hardCapSeconds = 600;
  let warnAtSeconds = 480;
  let reconnectAttempts = 0;
  let closed = false;

  function _emit(type, payload) {
    try { onEvent && onEvent({ type, ...payload }); } catch { /* noop */ }
  }

  async function _startCall() {
    // 1. Mint the ephemeral session.
    const startResp = await api.post("/realtime/session");
    realtimeSessionId = startResp.data.realtime_session_id;
    clientSecret = startResp.data.client_secret;
    hardCapSeconds = startResp.data.hard_cap_seconds || hardCapSeconds;
    warnAtSeconds = startResp.data.warn_at_seconds || warnAtSeconds;
    _emit("session_created", { realtimeSessionId,
                                 hardCapSeconds, warnAtSeconds });

    // 2. getUserMedia.
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // 3. RTCPeerConnection.
    pc = new RTCPeerConnection();
    audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.style.display = "none";
    document.body.appendChild(audioEl);
    pc.ontrack = (ev) => {
      if (ev.streams && ev.streams[0]) {
        audioEl.srcObject = ev.streams[0];
      }
    };
    pc.onconnectionstatechange = () => {
      _emit("connection_state", { state: pc.connectionState });
      if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
        _attemptReconnect();
      }
    };
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream));

    // 4. Data channel BEFORE offer so it's in the SDP.
    dc = pc.createDataChannel("oai-events");
    dc.onmessage = (ev) => _handleOAIEvent(ev.data);
    dc.onopen = () => _emit("data_channel_open", {});

    // 5. SDP offer.
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // 6. Backend proxies the SDP negotiation. Ephemeral secret in header.
    const negResp = await api.post("/realtime/negotiate", offer.sdp, {
      headers: {
        "Content-Type": "application/sdp",
        "X-Realtime-Session-Id": realtimeSessionId,
        "X-OpenAI-Client-Secret": clientSecret,
      },
      transformRequest: [(data) => data],   // don't stringify our SDP
    });
    await pc.setRemoteDescription({ type: "answer", sdp: negResp.data.sdp });

    // 7. Heartbeat + hard cap.
    heartbeatTimer = setInterval(() => {
      api.post("/realtime/heartbeat",
                 { realtime_session_id: realtimeSessionId }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);

    hardCapTimer = setTimeout(() => {
      _emit("hard_cap_hit", {});
      end("hard_cap");
    }, hardCapSeconds * 1000);
    // Warn timer (only used by overlay for UI).
    setTimeout(() => _emit("warn_cap", {}), warnAtSeconds * 1000);

    _emit("started", {});
  }

  function _handleOAIEvent(raw) {
    let ev;
    try { ev = JSON.parse(raw); } catch { return; }
    const t = ev.type;
    if (!t) return;
    // Route to overlay via _emit, and persist finalized turns.
    if (t === "input_audio_buffer.speech_started") {
      _emit("player_speech_started", {});
    } else if (t === "input_audio_buffer.speech_stopped") {
      _emit("player_speech_stopped", {});
    } else if (t === "response.created") {
      _emit("thinking", {});
    } else if (t === "response.audio.delta") {
      _emit("speaking", {});
    } else if (t === "response.cancelled") {
      _emit("interrupted", {});
      // Stop any playing audio immediately.
      try { audioEl && audioEl.pause(); } catch { /* noop */ }
    } else if (t === "response.done") {
      _emit("assistant_turn_done", {});
    } else if (t === "conversation.item.input_audio_transcription.completed") {
      // Finalized player transcript.
      const text = ev.transcript || "";
      const itemId = ev.item_id;
      if (text && itemId) _persistTurn("user", text, itemId);
      _emit("player_transcript", { text, item_id: itemId });
    } else if (t === "response.audio_transcript.done") {
      // Finalized assistant transcript.
      const text = ev.transcript || "";
      const itemId = ev.item_id || ev.response_id;
      if (text && itemId) _persistTurn("assistant", text, itemId);
      _emit("assistant_transcript", { text, item_id: itemId });
    } else if (t === "error") {
      _emit("openai_error", { detail: ev.error });
    }
  }

  async function _persistTurn(role, content, openaiItemId) {
    if (!realtimeSessionId) return;
    try {
      await api.post("/realtime/turn", {
        realtime_session_id: realtimeSessionId,
        openai_item_id: String(openaiItemId),
        role,
        content,
      });
    } catch { /* silent — persistence is best-effort at UI-time */ }
  }

  async function _attemptReconnect() {
    if (closed) return;
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      _emit("reconnect_failed", {});
      await end("reconnect_failed");
      return;
    }
    reconnectAttempts += 1;
    _emit("reconnecting", { attempt: reconnectAttempts });
    // End current session cleanly server-side, then start fresh.
    await end("reconnect", { silent: true });
    closed = false;
    try {
      await _startCall();
    } catch (err) {
      _emit("reconnect_failed", { error: String(err) });
      await end("reconnect_failed");
    }
  }

  // Public API ------------------------------------------------------
  async function start() {
    if (!isRealtimeCapable()) throw new Error("unsupported_browser");
    try {
      await _startCall();
    } catch (err) {
      _emit("start_failed", { error: err?.response?.data?.detail
                                       || err?.message || String(err) });
      await end("start_failed", { silent: true });
      throw err;
    }
  }

  function muteMic(muted) {
    if (!localStream) return;
    localStream.getAudioTracks().forEach(t => { t.enabled = !muted; });
    _emit("mic_muted", { muted });
  }

  function muteOutput(muted) {
    if (!audioEl) return;
    audioEl.muted = muted;
    _emit("output_muted", { muted });
  }

  async function end(reason, opts = {}) {
    if (closed) return;
    closed = true;
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
    if (hardCapTimer) { clearTimeout(hardCapTimer); hardCapTimer = null; }
    try { dc && dc.close(); } catch { /* noop */ }
    try { pc && pc.close(); } catch { /* noop */ }
    try { localStream && localStream.getTracks().forEach(t => t.stop()); }
    catch { /* noop */ }
    try { if (audioEl && audioEl.parentNode) audioEl.parentNode.removeChild(audioEl); }
    catch { /* noop */ }
    if (realtimeSessionId) {
      try {
        await api.post("/realtime/end", {
          realtime_session_id: realtimeSessionId,
          terminated_reason: reason || "user_ended",
        });
      } catch { /* silent */ }
    }
    if (!opts.silent) _emit("ended", { reason });
  }

  return { start, muteMic, muteOutput, end };
}
